import { useParams } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { Suspense, useState } from 'react'
import {
  Button,
  Col,
  ConfigProvider,
  Form,
  Row,
  Space,
  theme,
  Typography,
} from 'antd'
import { errorResponse } from '../../lib/errorResponse'
import { gutter } from '../../lib/layout'
import { accountingSoftware } from './accountingSoftware'
import { LoadingAnimation } from '../LoadingAnimation'
import { FileInputFormItem } from './FileInputFormItem'
import dayjs from 'dayjs'
import { FilePdfOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { helpCenterLinks } from '../../config/helpCenterLinks'
import { useOnboardingHelpLink } from '../../lib/useOnboardingHelpLink'

export function StepUploadPreviousMonthPayslips({
  onboarding,
  sessionToken,
  goToNext,
  goToNextText,
  goToPrevious,
}) {
  const { companyUid } = useParams()
  const helpLink = useOnboardingHelpLink(companyUid)
  const [loading, setLoading] = useState(false)
  const { t } = useTranslation()

  const { token } = theme.useToken()

  async function onSubmit() {
    setLoading(true)
    try {
      goToNext()
    } catch (e) {
      errorResponse(e)
    } finally {
      setLoading(false)
    }
  }

  async function uploading(upload) {
    setLoading(upload)
  }

  const software = accountingSoftware[onboarding.software]

  const [form] = Form.useForm()

  const lastMonth = dayjs(onboarding.firstMonth)
    .subtract(1, 'month')
    .format('MMMM YYYY')
  return (
    <Form form={form} layout="vertical" onFinish={onSubmit}>
      <Row gutter={gutter}>
        <ConfigProvider
          theme={{
            components: {
              Button: {
                fontSize: token.fontSize,
                paddingContentHorizontal: token.paddingContentHorizontal,
                controlHeight: token.controlHeight,
                borderRadius: token.borderRadius,
              },
            },
          }}
        >
          <Col span={24}>
            <Typography.Paragraph>
              <Trans
                i18nKey="frontend:onboarding.previousMonthPayslipsUploadDescription"
                components={{ strong: <strong /> }}
                values={{ lastMonth }}
              />
            </Typography.Paragraph>
          </Col>
          {onboarding.software !== 'misc' && (
            <Col span={24}>
              <Button
                style={{ height: 'auto' }}
                icon={<QuestionCircleOutlined />}
                onClick={() => {
                  window.open(
                    helpLink(
                      helpCenterLinks.de.onboarding
                        .stepUploadPreviousMonthPayslips[onboarding.software]
                    ),
                    '_blank'
                  )
                }}
              >
                <span style={{ whiteSpace: 'normal' }}>
                  {t('frontend:onboarding.previousMonthPayslipsHelpQuestion', {
                    software: software?.label,
                  })}
                </span>
              </Button>
            </Col>
          )}
          <Col span={24}>
            <Suspense fallback={<LoadingAnimation />}>
              <FileInputFormItem
                buttonText={
                  <Space>
                    <FilePdfOutlined />
                    <span>
                      {t(
                        'frontend:onboarding.previousMonthPayslipsChooseFile',
                        {
                          lastMonth,
                        }
                      )}
                    </span>
                  </Space>
                }
                name={['fileList']}
                parentPath={`onboarding/${companyUid}/previousMonthPayslips/`}
                accept={['.pdf']}
                maxCount={1}
                setLoading={uploading}
                sessionToken={sessionToken}
              />
            </Suspense>
          </Col>
        </ConfigProvider>
        <Col span={24} style={{ textAlign: 'right' }}>
          <Space>
            <Button type="text" onClick={() => goToPrevious()}>
              {t('frontend:common.back')}
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {goToNextText}
            </Button>
          </Space>
        </Col>
      </Row>
    </Form>
  )
}
